import { useState, useEffect, useCallback } from 'react'
import { customerApi } from '../services/api'
import toast from 'react-hot-toast'
import PeriodSelector from '../components/analytics/PeriodSelector'
import ExportButton from '../components/analytics/ExportButton'
import StatCard from '../components/analytics/StatCard'
import ConversationChart from '../components/analytics/ConversationChart'
import TopQuestionsList from '../components/analytics/TopQuestionsList'
import AgentDistributionChart from '../components/analytics/AgentDistributionChart'
import PeakHoursChart from '../components/analytics/PeakHoursChart'
import {
  ChatBubbleLeftRightIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  ArrowsRightLeftIcon,
  CheckBadgeIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

export default function CustomerAnalytics() {
  const [period, setPeriod] = useState('30d')
  const [loading, setLoading] = useState(true)
  const [overview, setOverview] = useState(null)
  const [conversationsData, setConversationsData] = useState([])
  const [topQuestions, setTopQuestions] = useState({ questions: [], total_questions: 0 })
  const [agentDist, setAgentDist] = useState({ agents: [], total: 0 })
  const [quality, setQuality] = useState(null)
  const [peakHours, setPeakHours] = useState({ hours: [], peak_hour: 0, peak_label: '' })
  const [lastUpdated, setLastUpdated] = useState(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const [overviewRes, convRes, questionsRes, agentRes, qualityRes, peakRes] = await Promise.all([
        customerApi.get(`/customer/analytics/overview?period=${period}`),
        customerApi.get(`/customer/analytics/conversations-over-time?period=${period}`),
        customerApi.get(`/customer/analytics/top-questions?period=${period}`),
        customerApi.get(`/customer/analytics/agent-distribution?period=${period}`),
        customerApi.get(`/customer/analytics/response-quality?period=${period}`),
        customerApi.get(`/customer/analytics/peak-hours?period=${period}&tz=${Intl.DateTimeFormat().resolvedOptions().timeZone}`),
      ])

      setOverview(overviewRes.data)
      setConversationsData(convRes.data.data || [])
      setTopQuestions(questionsRes.data)
      setAgentDist(agentRes.data)
      setQuality(qualityRes.data)
      setPeakHours(peakRes.data)
      setLastUpdated(new Date())
    } catch (err) {
      toast.error('Failed to load analytics')
    } finally {
      setLoading(false)
    }
  }, [period])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const hasData = overview && overview.total_conversations > 0

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Analytics</h2>
          <div className="flex items-center space-x-3">
            <PeriodSelector selected={period} onChange={setPeriod} />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
              <div className="h-10 w-10 bg-slate-100 rounded-lg mb-3" />
              <div className="h-7 w-20 bg-slate-100 rounded mb-1" />
              <div className="h-4 w-24 bg-slate-50 rounded" />
            </div>
          ))}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
          <div className="h-4 w-40 bg-slate-100 rounded mb-4" />
          <div className="h-64 bg-slate-50 rounded-lg" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Analytics</h2>
          {lastUpdated && (
            <p className="text-xs text-slate-400 mt-0.5">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
          )}
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchAll}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <ArrowPathIcon className="h-4 w-4" />
          </button>
          <PeriodSelector selected={period} onChange={setPeriod} />
          <ExportButton period={period} />
        </div>
      </div>

      {!hasData ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ChatBubbleLeftRightIcon className="h-8 w-8 text-slate-300" />
          </div>
          <h3 className="text-base font-semibold text-slate-900 mb-1">No analytics data yet</h3>
          <p className="text-sm text-slate-500 max-w-sm mx-auto">
            Start chatting with your widget to see analytics here. Data will appear after your first conversation.
          </p>
        </div>
      ) : (
        <>
          {/* Stat Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Total Conversations"
              value={overview.total_conversations}
              change={overview.comparison?.conversations_change}
              changeType={overview.comparison?.conversations_change >= 0 ? 'increase' : 'decrease'}
              icon={ChatBubbleLeftRightIcon}
            />
            <StatCard
              title="Total Messages"
              value={overview.total_messages}
              change={overview.comparison?.messages_change}
              changeType={overview.comparison?.messages_change >= 0 ? 'increase' : 'decrease'}
              icon={ChatBubbleOvalLeftEllipsisIcon}
            />
            <StatCard
              title="Avg per Conversation"
              value={overview.avg_messages_per_conversation}
              icon={ArrowsRightLeftIcon}
            />
            <StatCard
              title="Success Rate"
              value={quality ? `${quality.success_rate}%` : 'N/A'}
              icon={CheckBadgeIcon}
            />
          </div>

          {/* Conversations Chart */}
          <ConversationChart data={conversationsData} />

          {/* Top Questions + Agent Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TopQuestionsList questions={topQuestions.questions} totalQuestions={topQuestions.total_questions} />
            <AgentDistributionChart agents={agentDist.agents} />
          </div>

          {/* Peak Hours */}
          <PeakHoursChart hours={peakHours.hours} peakHour={peakHours.peak_hour} />
        </>
      )}
    </div>
  )
}
